import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PageHead, Card } from "@/pages/app/AppShell";
import { api, errMsg, fmtDate } from "@/lib/api";

const CATS = ["Brand Partnerships", "Sponsorship", "Fashion", "Sports", "Entertainment", "Technology", "Luxury", "Influencer Marketing"];
const blank = { title: "", category: CATS[0], excerpt: "", body: "", cover_image: "", author: "", status: "published" };

export default function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(null);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/news/admin/all").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = Object.fromEntries(Object.keys(blank).map((k) => [k, f[k] ?? ""]));
    try {
      if (editId) await api.put(`/news/${editId}`, body); else await api.post("/news", body);
      toast.success("Story saved");
      setF(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="news-admin-page">
      <PageHead kicker="Balqis News" title="Newsroom"><button className="btn-gold" onClick={() => { setEditId(null); setF(blank); }} data-testid="news-new-button"><Plus className="h-4 w-4" />New story</button></PageHead>
      <Card className="divide-y divide-[#2A1240]/10">
        {!items.length && <p className="p-8 text-center text-slate-400">No stories yet.</p>}
        {items.map((n) => (
          <div key={n.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`news-admin-row-${n.id}`}>
            <div><p className="text-[11px] uppercase tracking-[0.2em] text-[#8E2BC4]">{n.category} · {n.status} · {fmtDate(n.published_at)}</p><p className="mt-1 font-display text-xl text-[#2A1240]">{n.title}</p></div>
            <div className="flex gap-2">
              <button className="btn-ghost !py-2 !px-3 !text-xs" onClick={() => { setEditId(n.id); setF({ ...blank, ...n }); }} data-testid={`news-edit-${n.id}`}><Pencil className="h-3.5 w-3.5" />Edit</button>
              <button className="btn-ghost !py-2 !px-3 !text-xs" onClick={() => window.confirm("Delete story?") && api.delete(`/news/${n.id}`).then(load)} data-testid={`news-delete-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </Card>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        {f && (
          <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10 max-w-2xl max-h-[92vh] overflow-y-auto" data-lenis-prevent data-testid="news-dialog">
            <DialogTitle className="font-display text-2xl font-normal">{editId ? "Edit story" : "New story"}</DialogTitle>
            <input className="field" placeholder="Headline" value={f.title} onChange={set("title")} data-testid="news-title-input" />
            <div className="grid grid-cols-2 gap-3">
              <select className="field" value={f.category} onChange={set("category")} data-testid="news-category-select">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
              <select className="field" value={f.status} onChange={set("status")} data-testid="news-status-select"><option value="published">Published</option><option value="draft">Draft</option></select>
            </div>
            <input className="field" placeholder="Excerpt" value={f.excerpt} onChange={set("excerpt")} data-testid="news-excerpt-input" />
            <input className="field" placeholder="Cover image URL (optional)" value={f.cover_image} onChange={set("cover_image")} data-testid="news-cover-input" />
            <input className="field" placeholder="Author (defaults to you)" value={f.author} onChange={set("author")} data-testid="news-author-input" />
            <textarea rows={10} className="field" placeholder="Story — separate paragraphs with a blank line" value={f.body} onChange={set("body")} data-testid="news-body-input" />
            <button className="btn-gold" disabled={f.title.length < 4 || f.body.length < 20} onClick={save} data-testid="news-save-button">Save story</button>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
